/**
 * UpNextCard — compact live countdown row for an upcoming quiz/assignment
 * (slim horizontal card: accent edge, title + deadline label, ticking timer,
 * bell toggle for reminders). Disappears server-side once the student has
 * submitted, so only outstanding work counts down.
 */
import React, { useEffect, useState } from 'react';
import { UpcomingQuizItem } from '../../api/courses';
import {
  isReminderSet,
  toggleReminder,
  ensureNotificationPermission,
} from '../../utils/reminders';

interface Props {
  item: UpcomingQuizItem;
  /** Optional click-through (e.g. open the course). */
  onOpen?: () => void;
  className?: string;
}

const pad = (n: number) => n.toString().padStart(2, '0');

/** "STARTS TOMORROW", "DUE TODAY", "DUE IN 3 DAYS"... */
const deadlineLabel = (item: UpcomingQuizItem, msLeft: number): string => {
  const verb = item.status === 'scheduled' ? 'Starts' : 'Due';
  const days = Math.floor(msLeft / 86_400_000);
  const deadline = new Date(item.next_deadline);
  const now = new Date();
  const isToday = deadline.toDateString() === now.toDateString();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (isToday) return `${verb} today`;
  if (deadline.toDateString() === tomorrow.toDateString()) return `${verb} tomorrow`;
  if (days >= 1) return `${verb} in ${days} ${days === 1 ? 'day' : 'days'}`;
  return `${verb} soon`;
};

const UpNextCard: React.FC<Props> = ({ item, onOpen, className = '' }) => {
  const [now, setNow] = useState(() => Date.now());
  const [remindOn, setRemindOn] = useState(() => isReminderSet(item.quiz_id));

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const msLeft = Math.max(0, new Date(item.next_deadline).getTime() - now);
  const days = Math.floor(msLeft / 86_400_000);
  const hours = Math.floor((msLeft % 86_400_000) / 3_600_000);
  const minutes = Math.floor((msLeft % 3_600_000) / 60_000);
  const seconds = Math.floor((msLeft % 60_000) / 1000);
  const urgent = msLeft < 24 * 3_600_000;

  const handleReminder = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!remindOn) {
      const ok = await ensureNotificationPermission();
      if (!ok) {
        window.alert('Notifications are blocked in your browser. Allow them for this site to get reminders.');
        return;
      }
    }
    setRemindOn(toggleReminder(item.quiz_id));
  };

  return (
    <div
      onClick={onOpen}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={(e) => { if (onOpen && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(); } }}
      className={`relative flex items-center gap-3 overflow-hidden rounded-xl border border-gray-200 bg-white py-2.5 pl-4 pr-3 shadow-soft dark:border-secondary-700 dark:bg-secondary-800 ${onOpen ? 'cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-medium' : ''} ${className}`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${urgent ? 'bg-red-500' : 'bg-amber-400'}`} />

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-gray-900 dark:text-white">
          {item.title}
          {item.item_type === 'assignment' && (
            <span className="ml-1.5 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
              Assignment
            </span>
          )}
        </p>
        <p className="truncate text-[11px] text-gray-500 dark:text-gray-400">
          {deadlineLabel(item, msLeft)} · {item.course_title}
        </p>
      </div>

      <span className={`flex-shrink-0 font-mono text-base font-bold tabular-nums ${urgent ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-100'}`}>
        {days > 0 ? `${days}d ` : ''}{pad(hours)}:{pad(minutes)}:{pad(seconds)}
      </span>

      <button
        onClick={handleReminder}
        title={remindOn ? 'Reminder on (click to turn off)' : 'Set reminder'}
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
          remindOn
            ? 'bg-red-600 text-white hover:bg-red-700'
            : 'text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/25 dark:hover:text-red-400'
        }`}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.66V5a2 2 0 10-4 0v.34A6 6 0 006 11v3.2a2 2 0 01-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      </button>
    </div>
  );
};

export default UpNextCard;
